// This date-utils file is allowed to work directly with Date.
/* eslint-disable no-restricted-globals -- Date */

import {
  DayObject,
  DayRange,
  DetailedTimeOfDay,
  END_OF_DAY,
  FixedDayRange,
  FixedTimeRange,
  START_OF_DAY,
  TimeOfDay,
  Timestamp,
} from "@/types/date"
import isEqual from "lodash/isEqual"
// Only this date-utils file works directly with luxon
// eslint-disable-next-line no-restricted-imports
import { DateTime } from "luxon"

import { assert } from "@/lib/utils/assertions"

const DEFAULT_TIMEZONE = "America/New_York"

export function now(): Timestamp {
  return { epoch_millis: nowMillis() }
}

export function nowMillis(): number {
  return Date.now()
}

export function nowISOString(): string {
  return new Date(nowMillis()).toISOString()
}

export function today(timeZone: string): DayObject {
  return getDayObject(now(), timeZone)
}

// Returns a DayObject representing the current day in the browser's
// time zone.
export function browserToday(): DayObject {
  return today(browserTimeZone())
}

//
//  DayObject
//

export function parseDayParam(
  dateStr: string | null | undefined
): DayObject | undefined {
  if (!dateStr) return undefined
  const parts = dateStr.split("-")
  if (parts.length !== 3) return undefined
  const year = parseInt(parts[0], 10)
  const month = parseInt(parts[1], 10)
  const day = parseInt(parts[2], 10)
  if (isNaN(year) || isNaN(month) || isNaN(day)) return undefined
  return { year, month, day }
}

// Return day object formatted for a URL parameter, like "2023-06-07"
export function dayObjectAsUrlParam(dayObject: DayObject): string {
  return `${dayObject.year}-${String(dayObject.month).padStart(2, "0")}-${String(dayObject.day).padStart(2, "0")}`
}

export function sameDay(day1: DayObject, day2: DayObject): boolean {
  return isEqual(day1, day2)
}

// Returns true if day1 is before day2.
export function beforeDayObject(day1: DayObject, day2: DayObject): boolean {
  return (
    !isEqual(day1, day2) &&
    getTimestamp(day1, END_OF_DAY, ARBITRARY_TIME_ZONE).epoch_millis <
      getTimestamp(day2, START_OF_DAY, ARBITRARY_TIME_ZONE).epoch_millis
  )
}

// Returns true if day1 is after day2.
export function afterDayObject(day1: DayObject, day2: DayObject): boolean {
  return !sameDay(day1, day2) && !beforeDayObject(day1, day2)
}

// Sorts day objects in ascending order, with undefineds at the end.
export function compareDayObjects(
  dayA: DayObject | undefined,
  dayB: DayObject | undefined
): number {
  if (!dayA && !dayB) {
    return 0
  } else if (!dayA) {
    return 1
  } else if (!dayB) {
    return -1
  } else if (sameDay(dayA, dayB)) {
    return 0
  } else if (beforeDayObject(dayA, dayB)) {
    return -1
  } else {
    return 1
  }
}

export function minDayObject(a: DayObject, b: DayObject): DayObject {
  return compareDayObjects(a, b) <= 0 ? a : b
}

export function maxDayObject(a: DayObject, b: DayObject): DayObject {
  return compareDayObjects(a, b) >= 0 ? a : b
}

export function nightsUntilDayObject(day: DayObject, timeZone: string): number {
  return nightsBetweenDayObjects(today(timeZone), day)
}

export function nightsBetweenDayObjects(
  day1: DayObject,
  day2: DayObject
): number {
  const zone = ARBITRARY_TIME_ZONE
  const time1 = getTimestamp(day1, START_OF_DAY, zone)
  const time2 = getTimestamp(day2, START_OF_DAY, zone)
  return nightsBetween(time1, time2, zone)
}

// Returns a number 1-7 representing the day of the week, where 1 is
// Monday and 7 is Sunday.
//
// To get a range 0-6 where 0=Sunday, use mod 7 on the result:
//   weekday(time, timeZone) % 7
export function weekday(day: DayObject): number {
  return _getLuxonDateTimeFromDay(day, START_OF_DAY, ARBITRARY_TIME_ZONE)
    .weekday
}

// Given a DayObject, returns a new DayObject representing the
// following day.
export function dayAfter(day: DayObject): DayObject {
  return addDays(day, 1)
}

// Given a DayObject, returns a new DayObject representing the
// previous day.
export function dayBefore(day: DayObject): DayObject {
  return addDays(day, -1)
}

// Returns a new DayObject by adding a number of days to
// the given day object. The number of days can be negative.
export function addDays(day: DayObject, count: number): DayObject {
  const dt = _getLuxonDateTimeFromDay(
    day,
    ARBITRARY_TIME_OF_DAY,
    ARBITRARY_TIME_ZONE
  ).plus({
    days: count,
  })
  return getDayObject(dt.toMillis(), ARBITRARY_TIME_ZONE)
}

// Returns a new DayObject by adding a number of months to
// the given day object. The number of months can be negative.
export function addMonths(day: DayObject, count: number): DayObject {
  const dt = _getLuxonDateTimeFromDay(
    day,
    ARBITRARY_TIME_OF_DAY,
    ARBITRARY_TIME_ZONE
  ).plus({
    months: count,
  })
  return getDayObject(dt.toMillis(), ARBITRARY_TIME_ZONE)
}

// Returns the first day of the month containing the given day.
export function startOfMonth(day: DayObject): DayObject {
  return { ...day, day: 1 }
}

// Returns the last day of the month containing the given day.
export function endOfMonth(day: DayObject): DayObject {
  return addDays(addMonths(startOfMonth(day), 1), -1)
}

export function getDayObject(
  timestamp: Timestamp | Date | number,
  timeZone: string
): DayObject {
  const dt = _getLuxonDateTime(timestamp, timeZone)
  if (!dt.isValid) {
    // Fallback to current time in default timezone if invalid
    const fallbackDt = DateTime.now().setZone(DEFAULT_TIMEZONE)
    return {
      year: fallbackDt.year,
      month: fallbackDt.month,
      day: fallbackDt.day,
    }
  }
  return { year: dt.year, month: dt.month, day: dt.day }
}

//
//  Timestamp
//

export function equalTimestamps(time1: Timestamp, time2: Timestamp): boolean {
  return time1.epoch_millis === time2.epoch_millis
}

export function isToday(time: Timestamp, timeZone: string): boolean {
  return sameDay(getDayObject(time, timeZone), today(timeZone))
}

export function durationInSeconds(time1: Timestamp, time2: Timestamp): number {
  return Math.round((time2.epoch_millis - time1.epoch_millis) / 1000)
}

export function durationInMinutes(time1: Timestamp, time2: Timestamp): number {
  return Math.round((time2.epoch_millis - time1.epoch_millis) / (60 * 1000))
}

export function timestampsOnSameDay(
  time1: Timestamp | number,
  time2: Timestamp | number,
  timeZone: string
): boolean {
  return sameDay(getDayObject(time1, timeZone), getDayObject(time2, timeZone))
}

// Returns true if time1 is before time2.
export function beforeTimestamp(time1: Timestamp, time2: Timestamp): boolean {
  return time1.epoch_millis < time2.epoch_millis
}

// Returns true if time1 is after time2.
export function afterTimestamp(time1: Timestamp, time2: Timestamp): boolean {
  return time1.epoch_millis > time2.epoch_millis
}

// For sorting Timestamp in ascending orders, with undefineds at the end.
export function compareTimestamps(
  timeA: Timestamp | undefined,
  timeB: Timestamp | undefined
): number {
  if (!timeA && !timeB) {
    return 0
  } else if (!timeA) {
    return 1
  } else if (!timeB) {
    return -1
  } else {
    return timeA.epoch_millis - timeB.epoch_millis
  }
}

export function nightsBetween(
  time1: Timestamp,
  time2: Timestamp,
  timeZone: string
): number {
  const dt1 = _getLuxonDateTime(time1, timeZone).startOf("day")
  const dt2 = _getLuxonDateTime(time2, timeZone).startOf("day")
  return Math.ceil(dt2.diff(dt1, "days").as("day"))
}

export function hoursBetween(time1: Timestamp, time2: Timestamp): number {
  return Math.round(minutesBetween(time1, time2) / 60)
}

export function hoursBetweenDecimal(
  time1: Timestamp,
  time2: Timestamp
): number {
  return (1.0 * (time2.epoch_millis - time1.epoch_millis)) / (60 * 60 * 1000)
}

export function minutesBetween(time1: Timestamp, time2: Timestamp): number {
  return Math.floor(millisBetween(time1, time2) / (60 * 1000))
}

export function secondsBetween(time1: Timestamp, time2: Timestamp): number {
  return Math.floor(millisBetween(time1, time2) / 1000)
}

export function millisBetween(time1: Timestamp, time2: Timestamp): number {
  return time2.epoch_millis - time1.epoch_millis
}

// Returns a number 1-7 representing the day of the week, where 1 is
// Monday and 7 is Sunday.
//
// To get a range 0-6 where 0=Sunday, use mod 7 on the result:
//   weekdayOfTimestamp(time, timeZone) % 7
export function weekdayOfTimestamp(time: Timestamp, timeZone: string): number {
  return _getLuxonDateTime(time, timeZone).weekday
}

export function isOnDay(time: Timestamp, day: DayObject, timeZone: string) {
  const startOfDay = getTimestamp(day, START_OF_DAY, timeZone)
  const endOfDay = getTimestamp(day, END_OF_DAY, timeZone)
  return !beforeTimestamp(time, startOfDay) && !afterTimestamp(time, endOfDay)
}

// Adds a raw number of days to the given timestamp without trying
// to preserve the time of day. The number of days can be negative.
export function addDaysToTimestamp(
  timestamp: Timestamp,
  days: number
): Timestamp {
  return addHours(timestamp, Math.round(days * 24))
}

export function addHours(timestamp: Timestamp, hours: number): Timestamp {
  return addMinutes(timestamp, Math.round(hours * 60))
}

export function addMinutes(timestamp: Timestamp, minutes: number): Timestamp {
  return addMillis(timestamp, Math.round(minutes * 60 * 1000))
}

export function addSeconds(timestamp: Timestamp, seconds: number): Timestamp {
  return addMillis(timestamp, Math.round(seconds * 1000))
}

export function addMillis(timestamp: Timestamp, millis: number): Timestamp {
  return createTimestamp(timestamp.epoch_millis + millis)
}

// Creates a new timestamp with the same time as the given timestamp
// but on the given day.
export function changeDay(
  timestamp: Timestamp,
  newDay: DayObject,
  timeZone: string
): Timestamp {
  return getTimestamp(newDay, getTimeOfDay(timestamp, timeZone), timeZone)
}

export function getTimestamp(
  day: DayObject,
  timeOfDay: TimeOfDay,
  timeZone: string
): Timestamp {
  return createTimestamp(
    _getLuxonDateTimeFromDay(day, timeOfDay, timeZone).toMillis()
  )
}

export function createTimestamp(epochTime: number | Date): Timestamp {
  return { epoch_millis: getEpochMillis(epochTime) }
}

export function getEpochMillis(time: Timestamp | Date | number): number {
  if (typeof time === "number") {
    return time
  } else if (time instanceof Date) {
    return time.getTime()
  } else {
    return time.epoch_millis
  }
}

export function isCurrentYear(timestamp: Timestamp) {
  const currentYear = DateTime.now().year
  return DateTime.fromMillis(timestamp.epoch_millis).year === currentYear
}

//
//  TimeOfDay
//

// Returns true iff the first time is before the second.
export function beforeTimeOfDay(time1: TimeOfDay, time2: TimeOfDay): boolean {
  const t1: DetailedTimeOfDay = toDetailedTimeOfDay(time1)
  const t2: DetailedTimeOfDay = toDetailedTimeOfDay(time2)

  // No need to worry about daylight saving time here. A time of day is
  // ambiguous on days when the clocks change, anyway (e.g. there may be
  // two 2:30ams on the day of falling back). Just do a simple comparison.

  if (t1.hour < t2.hour) return true
  if (t1.hour > t2.hour) return false

  if (t1.minute < t2.minute) return true
  if (t1.minute > t2.minute) return false

  if (t1.second < t2.second) return true
  if (t1.second > t2.second) return false

  return t1.millisecond < t2.millisecond
}

export function compareTimesOfDay(
  timeA: TimeOfDay | undefined,
  timeB: TimeOfDay | undefined
): number {
  if (!timeA && !timeB) {
    return 0
  } else if (!timeA) {
    return 1
  } else if (!timeB) {
    return -1
  } else if (isEqual(timeA, timeB)) {
    return 0
  } else if (beforeTimeOfDay(timeA, timeB)) {
    return -1
  } else {
    return 1
  }
}

// Returns a new time of day by adding a number of hours. The number
// of hours can be negative.
export function plusHours(timeOfDay: TimeOfDay, hours: number): TimeOfDay {
  const timestamp = getTimestamp(ARBITRARY_DAY, timeOfDay, ARBITRARY_TIME_ZONE)
  return getTimeOfDay(addHours(timestamp, hours), ARBITRARY_TIME_ZONE)
}

// Creates a new timestamp with the same day as the given timestamp
// but at the given time of day.
export function changeTimeOfDay(
  timestamp: Timestamp,
  newTime: TimeOfDay,
  timeZone: string
): Timestamp {
  const day = getDayObject(timestamp, timeZone)
  return getTimestamp(day, newTime, timeZone)
}

export function getTimeOfDay(
  timestamp: Timestamp | Date | number,
  timeZone: string
): DetailedTimeOfDay {
  const { hour, minute, second, millisecond } = _getLuxonDateTime(
    timestamp,
    timeZone
  )
  return { hour, minute, second, millisecond }
}

export function toDetailedTimeOfDay(timeOfDay: TimeOfDay): DetailedTimeOfDay {
  return { ...START_OF_DAY, ...timeOfDay }
}

export function isTimeOfDayMidnight(timeOfDay: TimeOfDay): boolean {
  return (
    timeOfDay.hour === 0 &&
    timeOfDay.minute === 0 &&
    timeOfDay.second === 0 &&
    timeOfDay.millisecond === 0
  )
}

//
//  External Compatibility
//

// Create a Date for the given DayObject, for use with *external* APIs
// that expect a Date.
export function createJSDate(
  day: DayObject,
  timeOfDay: TimeOfDay,
  timeZone: string
) {
  return DateTime.now()
    .setZone(timeZone)
    .set(day)
    .set(toDetailedTimeOfDay(timeOfDay))
    .toJSDate()
}

// Create a Date for the given Timestamp, for use with *external* APIs
// that expect a Date.
export function createJSDateFromTimestamp(timestamp: Timestamp) {
  return new Date(timestamp.epoch_millis)
}

//
//  DayRange
//

// Returns the number of days in the given day range (inclusive of start and end),
// or undefined if either end of the range is open.
export function daysInDayRange(dayRange: DayRange) {
  return (
    dayRange.from &&
    dayRange.to &&
    1 + nightsBetweenDayObjects(dayRange.from, dayRange.to)
  )
}

export function rangeContainsDay(
  dayRange: FixedDayRange,
  day: DayObject
): boolean {
  return (
    !beforeDayObject(day, dayRange.from) && !afterDayObject(day, dayRange.to)
  )
}

export function rangeContainsRange(
  container: FixedDayRange,
  containee: FixedDayRange
): boolean {
  return (
    !beforeDayObject(containee.from, container.from) &&
    !afterDayObject(containee.to, container.to)
  )
}

export function dayRangeToDayArray(dayRange: DayRange): DayObject[] {
  if (!dayRange.from || !dayRange.to) {
    return []
  }
  return Array.from(iterableDayRange(dayRange))
}

// Turn a date range into a sequence of days, inclusive of start and end.
//   for (const day of iterableDayRange(dateRange)) {
//   }
//
// A range with no start iterates nothing. A range with no end iterates
// forever.
//
export function iterableDayRange(range: DayRange): Iterable<DayObject> {
  function makeIterator(): Iterator<DayObject> {
    if (range.from && range.to && range.to < range.from) {
      throw new Error(`invalid date range: ${range}`)
    }

    let nextValue = range.from

    return {
      next() {
        if (nextValue) {
          const response = { value: nextValue, done: false }

          if (range.to && isEqual(nextValue, range.to)) {
            nextValue = undefined // We've reached the end
          } else {
            nextValue = dayAfter(nextValue)
          }

          return response
        } else {
          return { value: undefined, done: true }
        }
      },
    }
  }

  return {
    [Symbol.iterator]: () => makeIterator(),
  }
}

let cachedBrowserTimeZone: string | undefined

/**
 * Resolved once: a document's zone cannot change, and browserToday() runs on
 * every card render inside usePlaceSearchParams — ~300 times on a 96-card grid.
 */
export function browserTimeZone(): string {
  if (cachedBrowserTimeZone !== undefined) {
    return cachedBrowserTimeZone
  }

  cachedBrowserTimeZone = DEFAULT_TIMEZONE
  try {
    const resolved = Intl.DateTimeFormat().resolvedOptions().timeZone
    // Luxon has to recognize the zone, or every date built from it is invalid.
    if (resolved && DateTime.now().setZone(resolved).isValid) {
      cachedBrowserTimeZone = resolved
    }
  } catch {
    // Keep the fallback.
  }
  return cachedBrowserTimeZone
}

// useful for http query params
export function dayToISO(day: DayObject): string {
  return (
    _getLuxonDateTimeFromDay(
      day,
      START_OF_DAY,
      ARBITRARY_TIME_ZONE
    ).toISODate() ?? ""
  )
}

function _getLuxonDateTime(
  timestamp: Timestamp | Date | number,
  timeZone: string
): DateTime {
  const epochMillis = getEpochMillis(timestamp)
  return DateTime.fromMillis(epochMillis).setZone(timeZone)
}

function _getLuxonDateTimeFromDay(
  day: DayObject,
  timeOfDay: TimeOfDay,
  timeZone: string
): DateTime {
  assert(timeZone, "timeZone is required")
  return DateTime.now()
    .setZone(timeZone)
    .set(day)
    .set({ second: 0, millisecond: 0, ...timeOfDay })
}

// Use this internally in date utils if needed for working with
// APIs that need a time zone, but only if the zone from our
// perspective is truly unimportant. We should be able to change
// this constant at any time -- from UTC to America/Los_Angeles,
// for example -- without breaking anything.
const ARBITRARY_TIME_ZONE = "UTC"
const ARBITRARY_DAY: DayObject = {
  year: 2000,
  month: 1,
  day: 1,
}
const ARBITRARY_TIME_OF_DAY: TimeOfDay = {
  hour: 0,
  minute: 0,
  second: 0,
  millisecond: 0,
}

// Function to convert Timestamp to ICS date array in UTC
export function convertToICSDateArray(
  timestamp: Timestamp
): [number, number, number, number, number] {
  const dt = DateTime.fromMillis(timestamp.epoch_millis).toUTC()
  return [dt.year, dt.month, dt.day, dt.hour, dt.minute]
}

export function convertDayObjectToTimestamp(
  day: DayObject,
  timeZone: string,
  endOfDay = false
): Timestamp {
  const dt = DateTime.fromObject(
    {
      year: day.year,
      month: day.month,
      day: day.day,
      hour: endOfDay ? 23 : 0,
      minute: endOfDay ? 59 : 0,
      second: endOfDay ? 59 : 0,
      millisecond: endOfDay ? 999 : 0,
    },
    { zone: timeZone }
  )
  return {
    epoch_millis: dt.toMillis(),
  }
}

export function convertDayRangeToFixedTimeRange(
  range: DayRange,
  timeZone: string
): FixedTimeRange | undefined {
  if (!range.from || !range.to) return undefined

  return {
    from: convertDayObjectToTimestamp(range.from, timeZone),
    to: convertDayObjectToTimestamp(range.to, timeZone, true),
  }
}

export function convertISODateToTimestamp(isoDate: string): Timestamp {
  const dt = DateTime.fromISO(isoDate)
  return { epoch_millis: dt.toMillis() }
}

// Parse a HUMAN-READABLE date string like "Dec 15, 2025" and return a
// DayObject. Timezone-independent for that format (JS parses it as local
// time, and the getters read local time back). NOT for ISO "YYYY-MM-DD"
// strings: JS parses those as UTC midnight, which the getters would read
// back a day early anywhere west of UTC — use parseDayParam for them.
// Module-private so that trap stays unreachable; parseMonthString is the
// only consumer.
function parseDateString(dateStr: string): DayObject | undefined {
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) {
    return undefined
  }
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  }
}

// Parse a month string like "Jan 2026" and return a FixedTimeRange
export function parseMonthString(
  monthStr: string,
  timeZone: string
): FixedTimeRange | undefined {
  // add "1, " to make it a valid date like "Jan 1, 2026"
  const monthDay = parseDateString(`${monthStr.replace(" ", " 1, ")}`)
  if (!monthDay) {
    return undefined
  }

  const monthStart = startOfMonth(monthDay)
  const monthEnd = endOfMonth(monthDay)

  return {
    from: getTimestamp(monthStart, START_OF_DAY, timeZone),
    to: getTimestamp(monthEnd, END_OF_DAY, timeZone),
  }
}
